/**
 * UI store for the taskpane.
 * Exports the `Toast` type and the `useUiStore` hook.
 */
import { create } from 'zustand';

/**
 * A single toast notification.
 */
export interface Toast {
  id: string;
  message: string;
  level: 'success' | 'error' | 'info';
  durationMs?: number; // default 4000
}

/**
 * Active tab in the taskpane UI.
 */
export type ActiveTab = 'tools' | 'templates' | 'bg-images';

interface UiStore {
  toasts: Toast[];
  activeTab: ActiveTab;
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
  setActiveTab: (tab: ActiveTab) => void;
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const arr = new Uint32Array(2);
    crypto.getRandomValues(arr);
    return `${Date.now().toString(36)}-${arr[0].toString(36)}${arr[1].toString(36)}`;
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
};

const useUiStore = create<UiStore>((set) => ({
  toasts: [],
  activeTab: 'tools',
  showToast: (toast) => {
    const id = generateId();
    const durationMs = toast.durationMs ?? 4000;
    set((state) => ({
      toasts: [...state.toasts, { id, ...toast, durationMs }],
    }));
  },
  dismissToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
  setActiveTab: (tab) => set({ activeTab: tab }),
}));

export default useUiStore;
